import classnames from 'classnames';
import React, { MouseEvent, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import WidgetModel from '@mm/state/model/WidgetModel';
import { CropArea } from '../../components/CropArea/CropArea';
import Spinner from '../../components/Spinner';
import { useImageWidgetHandlers } from '../../helpers/widget-helpers/use-image-widget-handlers';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './ImageWidget.pcss';
import { Alert } from '@mm/components';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  smsOnly?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

const useUploadImage = (widget: WidgetModel) => {
  const imageWidgetHandlers = useImageWidgetHandlers();
  const [isUploading, setIsUploading] = useState(false);

  const uploadImage = async (file: File | Blob) => {
    try {
      setIsUploading(true);
      await imageWidgetHandlers.addImageToWidget(widget, file);
      setIsUploading(false);
    } catch (error) {
      setIsUploading(false);
    }
  };

  return { isUploading, uploadImage };
};

const SMS_SIZE_CAP = 5 * 1024 * 1024;

export const ImageWidget = (props: Props) => {
  const { widget, draggableProvided, isSmsRelated, smsOnly, platforms } = props;
  const { uploadImage, isUploading } = useUploadImage(widget);
  const [showCropper, setShowCropper] = useState(false);
  const { file } = widget;
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept: 'image/*',
      onDrop: (files: File[]) => uploadImage(files[0]),
    });

  const onClickCrop = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCropper(true);
  };

  const { sourceSize } = widget.config;
  const isSizeOvercap = isSmsRelated && sourceSize && sourceSize > SMS_SIZE_CAP;

  return (
    <div
      className={classnames('image-widget', {
        'image-widget--has-image': file,
        'image-widget--active-drag': isDragActive,
        'image-widget--rejected-drag': isDragReject,
      })}
    >
      <FacebookPageIcon />

      <div className="image-widget__drop-area" {...getRootProps()}>
        {isUploading && (
          <div className="image-widget__uploading-wrapper">
            <Spinner
              show={true}
              position="absolute"
              backgroundColor="none"
              size={30}
            />
          </div>
        )}

        {file && (
          <img alt="Cropped Asset" src={file} className="image-widget__image" />
        )}

        <input {...getInputProps()} accept="image/*" />

        {file ? (
          <div className="image-widget__edit-image-controls">
            <div className="image-widget__edit-image-control">
              <i className="far fa-image fa-3x image-widget__edit-image-control-icon" />

              <span>
                Drop image file or
                <span className="image-widget__choose-file">choose file</span>
              </span>
            </div>

            <div
              onClick={onClickCrop}
              className="image-widget__edit-image-control"
            >
              <i className="fa fa-crop fa-3x image-widget__edit-image-control-icon" />

              <span>Crop image</span>
            </div>

            <div className="image-widget__edit-image-controls-blinder" />
          </div>
        ) : (
          <div className="image-widget__drop-area-text">
            <i className="far fa-image fa-3x image-widget__upload-icon" />
            <span>
              Drop image file or
              <span className="image-widget__choose-file">choose file</span>
            </span>
          </div>
        )}
      </div>

      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />

      {isSizeOvercap && (
        <Alert
          className="image-widget__warning"
          type={smsOnly ? 'error' : 'warning'}
          showCloseIcon={false}
        >
          This media file exceeds size limit on SMS and will NOT be sent in your
          SMS dialogue. Please reduce size to &lt;5MB.
        </Alert>
      )}

      {showCropper && (
        <CropArea
          imgUrl={file}
          onCropCallback={(blob: Blob) => uploadImage(blob)}
          closeCropper={() => setShowCropper(false)}
        />
      )}
    </div>
  );
};
